import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';

// Product categories
const categories = [
  {
    id: 'whole-spices',
    name: 'Whole Spices',
    description: 'Unground spices in their natural form, preserving maximum flavor and aroma.',
    image: '/Category/whole.avif',
  },
  {
    id: 'ground-spices',
    name: 'Ground Spices',
    description: 'Finely ground spices for immediate use in cooking, baking, and seasoning.',
    image: '/Category/ground.avif',
  },
  {
    id: 'spice-blends',
    name: 'Spice Blends',
    description: 'Expertly crafted combinations of spices for specific cuisines and dishes.',
    image: '/Category/blends.jpeg',
  },
  // {
  //   id: 'organic-spices',
  //   name: 'Organic Spices',
  //   description: 'Certified organic spices grown without synthetic pesticides or fertilizers.',
  //   image: 'https://images.unsplash.com/photo-1599690925058-90e1a0b56154?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80',
  // },
  {
    id: 'exotic-spices',
    name: 'Exotic Spices',
    description: 'Rare and unique spices from remote regions of the world.',
    image: '/Category/exotic.avif',
  },
  {
    id: 'doshi-ayucare',
    name: 'Doshi Ayucare',
    description: 'Premium Ayurvedic personal care products for holistic wellness.',
    image: '/Category/doshi.png',
  },
];

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};

const itemVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: {
      duration: 0.5
    }
  }
};

const ProductsPage: React.FC = () => {
  return (
    <div className="bg-primary-50">
      {/* Hero Section */}
      <section className="relative bg-spice-green text-white py-20">
        <div className="absolute inset-0 bg-black opacity-30"></div>
        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <h1 className="text-4xl md:text-5xl font-bold mb-6">Our Products</h1>
            <p className="text-xl text-primary-100">
              Explore our extensive collection of premium spices sourced from around the world.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Product Categories */}
      <section className="py-16 md:py-24">
        <div className="container-custom">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <h2 className="text-3xl font-bold mb-4">Product Categories</h2>
            <p className="text-lg text-primary-600 max-w-2xl mx-auto">
              Browse our range of spice categories to find exactly what you're looking for.
            </p>
          </motion.div>

          <motion.div 
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            {categories.map((category) => (
              <motion.div 
                key={category.id}
                className="bg-white rounded-lg overflow-hidden shadow-md hover:shadow-lg transition-shadow duration-300"
                variants={itemVariants}
              >
                <div className="h-64 overflow-hidden">
                  <img 
                    src={category.image} 
                    alt={category.name} 
                    className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-2xl font-bold mb-2">{category.name}</h3>
                  <p className="text-primary-600 mb-4">{category.description}</p>
                  <Link 
                    to={`/products/${category.id}`} 
                    className="btn btn-primary inline-block"
                  >
                    Explore {category.name}
                  </Link>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Quality Assurance */}
      <section className="py-16 bg-white">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-3xl font-bold mb-6">Our Quality Assurance</h2>
              <p className="text-lg text-primary-600 mb-4">
                Every spice in our collection undergoes rigorous quality testing to ensure purity, potency, and authenticity.
              </p>
              <p className="text-lg text-primary-600 mb-4">
                We carefully source our spices from trusted farmers and suppliers who share our commitment to sustainable and ethical practices.
              </p>
              <p className="text-lg text-primary-600">
                Our state-of-the-art processing facilities maintain the natural properties of each spice, preserving their essential oils, aroma, and flavor.
              </p>
            </motion.div>
            
            <motion.div
              className="rounded-lg overflow-hidden shadow-lg"
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <img 
                src="/cook.avif" 
                alt="Quality testing" 
                className="w-full h-full object-cover"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-12 bg-spice-red bg-opacity-10">
        <div className="container-custom">
          <motion.div 
            className="text-center"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <h3 className="text-2xl font-bold mb-4">Looking for Something Specific?</h3>
            <p className="text-lg text-primary-600 mb-6 max-w-2xl mx-auto">
              If you can't find what you're looking for or have questions about our products, our team is here to help.
            </p>
            <Link to="/contact" className="btn btn-primary px-8 py-3">
              Contact Us
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default ProductsPage; 